// GovernanceRoles — admin-plane role config (GOV-C, PAM Epic 11, Task 8).
// Lists current governance admin role assignments; supports proposing role
// assignments via dual-auth.
//
// OIDCPAM Gate-6 Phase 6a: migrated off MUI onto
// @devnw/cyber-iam/rbac/AdminRoleAssignmentPanel — the exact parity
// component built from this page. Assigning ALWAYS creates a dual-auth
// PROPOSAL (202) — the component says "proposed", never "assigned"; a
// second, distinct admin approves it in the Dual-Auth Queue
// (GovernanceDualAuth). `onUnassign` is intentionally omitted: no
// adminGovernanceUnassignRole endpoint exists in the generated client, so
// the component hides its Actions column entirely rather than showing a
// dead button.
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
// eslint-import-resolver-node (the resolver wired in .eslintrc.json) does not
// understand package.json "exports" maps, so it cannot follow @devnw's
// wildcard subpath export. The module resolves correctly at build/runtime
// (webpack, Node, ts-jest) via the "exports" field — see
// __tests__/devnw-smoke.test.tsx for the Phase 0 proof.
// eslint-disable-next-line import/no-unresolved
import AdminRoleAssignmentPanel from '@devnw/cyber-iam/rbac/AdminRoleAssignmentPanel/AdminRoleAssignmentPanel'
import type {
  AdminRoleAssignment,
  RoleDef,
} from '@devnw/cyber-iam/iam/types'
import { useAdminGenClient } from '../api/genClient'
import { useActiveTenant } from '../layout/TenantSwitcher'
import {
  toIamAdminRoleAssignment,
  type WireAdminRoleAssignment,
} from '../api/governanceRolesMapping'

function errMessage(e: unknown): string {
  return e instanceof Error ? e.message : 'Request failed'
}

// The governance admin-role assignment endpoint only ever accepts these 3
// mutually-exclusive kinds (server-enforced — AdminGovernanceAssignRole
// rejects any other `kind` with 400). Descriptions match the pre-migration
// MenuItem copy verbatim.
const ASSIGNABLE_ROLES: Array<Pick<RoleDef, 'name' | 'description'>> = [
  { name: 'security-admin', description: 'grants/revokes access' },
  { name: 'audit-admin', description: 'reads/exports audit records' },
  { name: 'policy-admin', description: 'manages governance policies' },
]

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => ut(asUnified(theme)).space('lg')};
`

const MutedText = styled.p`
  margin: 0;
  color: ${({ theme }) => asUnified(theme).text.secondary};
`

export const GovernanceRoles = (): JSX.Element => {
  const { tenant } = useActiveTenant()
  const client = useAdminGenClient()
  const [assignments, setAssignments] = useState<AdminRoleAssignment[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (): Promise<void> => {
    if (!tenant) return
    setLoading(true)
    setError(null)
    try {
      const data = await client.adminGovernanceListRoles({ tenantId: tenant })
      // The generated client's fromWire() camelCases the opaque
      // Array<Record<string, unknown>> response — see
      // ../api/governanceRolesMapping.ts for the real (camelCase) shape and
      // the latent snake_case-cast bug this mapping fixes.
      setAssignments(
        ((data.assignments ?? []) as unknown as WireAdminRoleAssignment[]).map(
          toIamAdminRoleAssignment
        )
      )
    } catch (e: unknown) {
      setError(errMessage(e))
    } finally {
      setLoading(false)
    }
  }, [tenant, client])

  useEffect(() => {
    load()
      .then(() => {})
      .catch(() => {})
  }, [load])

  const handleRefresh = useCallback((): void => {
    load()
      .then(() => {})
      .catch(() => {})
  }, [load])

  const handleAssign = async (
    subjectId: string,
    roleName: string
  ): Promise<void> => {
    if (!tenant) return
    // assign-role ALWAYS creates a dual-auth proposal (202) — it never
    // directly assigns the role. A second audit-admin must approve in the
    // Dual-Auth Queue. The component's own UI says "proposed" — never
    // "assigned" — reflecting this.
    await client.adminGovernanceAssignRole({
      tenantId: tenant,
      subjectId,
      kind: roleName,
    })
  }

  if (!tenant) {
    return (
      <PageContainer>
        <MutedText>Select a tenant to manage governance admin roles.</MutedText>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <AdminRoleAssignmentPanel
        testID="governance-roles"
        title="Admin Role Assignments (GOV-C)"
        description="Distinct admin roles enforce separation of duties. security-admin, audit-admin, and policy-admin are mutually exclusive — no single admin can both grant access and erase audit records. Sensitive assignments are subject to dual-auth approval."
        assignments={assignments}
        assignableRoles={ASSIGNABLE_ROLES}
        onAssign={handleAssign}
        onAssigned={handleRefresh}
        onRefresh={handleRefresh}
        loading={loading}
        error={error ?? undefined}
        emptyText="No governance admin role assignments."
      />
    </PageContainer>
  )
}
